import type { LifecycleStageId } from '../../components/dashboard/EvaluationLifecycleRoute';

export interface LifecycleStageDetail {
  id: LifecycleStageId;
  title: string;
  summary: string;
  bullets: string[];
}

export interface RoleBreakdown {
  id: string;
  title: string;
  scope: string;
  responsibilities: string[];
}

export interface SuiteCapability {
  title: string;
  description: string;
}

export interface SuiteArea {
  id: string;
  title: string;
  summary: string;
  capabilities: SuiteCapability[];
}

export const HERO_PILLARS = [
  {
    label: 'One workspace',
    detail: 'Shared navigation for day-to-day administrative work',
  },
  {
    label: 'Scoped access',
    detail: 'Role-based permissions for districts, schools, and staff',
  },
  {
    label: 'Operational modules',
    detail: 'Evaluations alongside slips, announcements, budgets, and more',
  },
  {
    label: 'Optional AI assist',
    detail: 'Evaluation scoring support when your organization enables it',
  },
];

export const PLATFORM_SUITE: SuiteArea[] = [
  {
    id: 'organization',
    title: 'Organization and access management',
    summary:
      'Structure your institution, assign people to the right schools, and control who can see or change each area of the platform.',
    capabilities: [
      {
        title: 'Districts and schools',
        description:
          'Maintain district and school records, contact details, and hierarchy for K–12 organizations using the platform.',
      },
      {
        title: 'Users and roles',
        description:
          'Invite administrators, evaluators, teachers, and other roles with permissions matched to their responsibilities.',
      },
      {
        title: 'Scoped permissions',
        description:
          'Limit lists, forms, and reports to the district or school each person is allowed to manage.',
      },
      {
        title: 'Administrative settings',
        description:
          'Central hub for organization preferences, including evaluation AI assist and data-sharing consent when your policy allows.',
      },
    ],
  },
  {
    id: 'communications',
    title: 'Communications and consent',
    summary:
      'Reach families and staff with announcements and digital consent workflows, with a separate parent-facing portal where it is enabled.',
    capabilities: [
      {
        title: 'Announcements',
        description:
          'Publish district- or school-level announcements with scheduling and approval flows where configured.',
      },
      {
        title: 'Permission slips',
        description:
          'Create and track field-trip and activity consent forms, responses, and status for administrative teams.',
      },
      {
        title: 'Parent portal',
        description:
          'Give parents a dedicated sign-in to view student-linked information and complete slips when your organization turns the portal on.',
      },
    ],
  },
  {
    id: 'resources',
    title: 'Budgets and inventory',
    summary:
      'Track fiscal categories and physical assets so finance and operations staff can work from the same system as instructional leaders.',
    capabilities: [
      {
        title: 'Budget planning',
        description:
          'View categories by fiscal year, allocated and spent amounts, utilization, and variance alerts for users with budget access.',
      },
      {
        title: 'Inventory management',
        description:
          'Search assets, record location and condition, and monitor maintenance and attention flags across buildings and rooms.',
      },
    ],
  },
  {
    id: 'evaluations',
    title: 'Evaluations and instructional support',
    summary:
      'Run structured teacher evaluation workflows, including Danielson-aligned rubrics, evidence, scoring, and conference-ready reports.',
    capabilities: [
      {
        title: 'Evaluation records',
        description:
          'Create and manage evaluations tied to teachers and schools, with status from draft through completion.',
      },
      {
        title: 'Observations and evidence',
        description:
          'Capture visit notes and attach artifacts that support rubric decisions before scores are finalized.',
      },
      {
        title: 'Rubric scoring and reports',
        description:
          'Score against district templates, generate evaluation reports, and retain history for conferencing and review.',
      },
    ],
  },
  {
    id: 'interventions',
    title: 'Interventions',
    summary:
      'Document and monitor student intervention plans—without automated risk prediction or AI-generated recommendations today.',
    capabilities: [
      {
        title: 'Intervention plans',
        description:
          'Create interventions manually with goals, responsible staff, frequency, and measurable targets.',
      },
      {
        title: 'Progress tracking',
        description:
          'Log progress notes and review intervention status from the interventions dashboard for authorized staff.',
      },
      {
        title: 'Intervention reporting',
        description:
          'Export intervention analytics where your role includes report access; predictive recommendations remain on the product roadmap.',
      },
    ],
  },
  {
    id: 'reporting',
    title: 'Reporting and analytics',
    summary:
      'Use live evaluation statistics and exportable summaries where implemented; preview areas are labeled clearly in the app.',
    capabilities: [
      {
        title: 'Evaluation analytics',
        description:
          'Dashboard charts for evaluation status and performance levels based on real data in your organization.',
      },
      {
        title: 'Reports hub',
        description:
          'Navigate to evaluations, export aggregate evaluation statistics, budget summaries, and intervention analytics when permitted.',
      },
      {
        title: 'Roadmap previews',
        description:
          'Some report types (such as rubric domain breakdowns and predictive student-risk views) are marked coming soon and show no fabricated data.',
      },
    ],
  },
];

export const LIFECYCLE_STAGES: LifecycleStageDetail[] = [
  {
    id: 'observation',
    title: 'Observation',
    summary: 'Plan and conduct classroom visits with structured note-taking.',
    bullets: [
      'Start evaluations linked to teachers and schools in your organization',
      'Record observation notes aligned to Danielson components',
      'Track status from draft through in-progress review',
    ],
  },
  {
    id: 'evidence',
    title: 'Evidence',
    summary: 'Collect artifacts that support rubric decisions.',
    bullets: [
      'Attach documents, links, and observation artifacts to each evaluation',
      'Keep evidence visible only to roles and schools your policy allows',
      'Build a clear record before scoring is finalized',
    ],
  },
  {
    id: 'rubric',
    title: 'Rubric',
    summary: 'Score against Danielson templates with evaluators in control.',
    bullets: [
      'Use district rubric templates and component-level scoring',
      'Optional AI assist for transcription and draft suggestions when enabled',
      'Evaluators confirm or override every score before submission',
    ],
  },
  {
    id: 'conference',
    title: 'Conference',
    summary: 'Close the loop with teachers and archived reports.',
    bullets: [
      'Generate evaluation reports for review and conferencing',
      'Maintain history per evaluation for follow-up across the year',
      'Support continuous improvement conversations with staff',
    ],
  },
];

export const ROLE_BREAKDOWN: RoleBreakdown[] = [
  {
    id: 'district_admin',
    title: 'District administrators',
    scope: 'Organization-wide',
    responsibilities: [
      'Configure districts, schools, and user access',
      'Set evaluation AI assist and data-sharing consent options',
      'Oversee evaluations, reports, and operational modules',
    ],
  },
  {
    id: 'principal',
    title: 'Principals and school leaders',
    scope: 'School-scoped',
    responsibilities: [
      'Monitor school-level evaluation activity',
      'Coordinate evaluators and teacher assignments',
      'Use announcements and reports for daily school operations',
    ],
  },
  {
    id: 'evaluator',
    title: 'Evaluators',
    scope: 'Evaluation workflow',
    responsibilities: [
      'Run observations and rubric scoring end to end',
      'Treat AI-assisted drafts as input to their final judgment',
      'Complete conferences and published reports',
    ],
  },
  {
    id: 'teacher',
    title: 'Teachers',
    scope: 'Personal evaluations',
    responsibilities: [
      'View evaluations and materials shared with their role',
      'Participate in conferencing per district policy',
      'Stay aligned with school improvement goals',
    ],
  },
  {
    id: 'parent',
    title: 'Parents and guardians',
    scope: 'Portal when enabled',
    responsibilities: [
      'Complete permission slips and view school communications',
      'Access student-linked portal features separate from staff tools',
    ],
  },
];

export const AI_PIPELINE_STEPS = [
  {
    step: '1',
    title: 'Capture',
    body: 'Observation audio or notes enter the pipeline when AI assist is enabled for your organization.',
  },
  {
    step: '2',
    title: 'Transcribe and align',
    body: 'Speech and text are processed to align with rubric components for evaluator review.',
  },
  {
    step: '3',
    title: 'Draft suggestions',
    body: 'The system may propose draft scores or language—never published without an evaluator’s review.',
  },
  {
    step: '4',
    title: 'Human decision',
    body: 'Evaluators edit, agree, or disagree; optional feedback can support model improvement where consent allows.',
  },
];
